import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import settings from './settings';
import Provider from './Provider';
import providerType from './providerType';

Modal.setAppElement('#page');

/**
 * Modal mount point.
 *
 * @returns {Element}
 */
const getModalMount = () => document.querySelector('#tve-provider-selection-modal'); // eslint-disable-line max-len

const Providers = ({
  all,
  isOpen,
  setIsOpen,
  primary,
  setLocalSelectedProvider,
}) => {
  const [search, setSearch] = useState('');

  // Find providers matching primary providers.
  const primaryList = useMemo(
    () => all.filter(
      (mvpd) => primary.includes(mvpd?.ID),
    ),
    [all, primary],
  );

  // Filter providers by search query.
  const allFiltered = all
    ? useMemo(
      () => all
        .filter(({ displayName }) => displayName
          .toLowerCase()
          .includes(search.toLowerCase())),
      [search, all],
    )
    : null;

  return (
    <Modal
      bodyOpenClassName="has-open-tve-select-provider-modal"
      className="tve-select-provider"
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      parentSelector={getModalMount}
      overlayClassName="tve-select-provider__overlay"
      shouldFocusAfterRender={false}
    >
      <div className="tve-select-provider__header">
        <button
          aria-label={settings('strings.select.close')}
          className="tve-select-provider__close"
          onClick={() => setIsOpen(false)}
          type="button"
        />

        <h2>{settings('strings.select.header')}</h2>

        <p className="tve-select-provider__description">
          {settings('strings.select.description')}
        </p>
      </div>

      <div className="tve-select-provider__primary">
        {primaryList.map((mvpd) => (
          <Provider
            ID={mvpd.ID}
            displayName={mvpd.displayName}
            key={mvpd.ID}
            logoURL={mvpd.logoURL}
            setLocalSelectedProvider={setLocalSelectedProvider}
          />
        ))}
      </div>

      {all ? (
        <div className="tve-select-provider__secondary">
          <h3>{settings('strings.select.other_header')}</h3>
          <input
            placeholder={settings('strings.select.other_placeholder')}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="tve-select-provider__secondary__list">
            {allFiltered ? (
              allFiltered.map((mvpd) => (
                <Provider
                  displayName={mvpd.displayName}
                  ID={mvpd.ID}
                  key={mvpd.ID}
                  setLocalSelectedProvider={setLocalSelectedProvider}
                />
              ))
            ) : (
              <div className="tve-select-provider__no-option">
                {settings('strings.select.none_found')}
                {' '}
                <a href={settings('options.no_tv_provider')}>
                  {settings('strings.select.none_found_link_text')}
                </a>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div className="tve-select-provider__no-provider">
        <a
          className="tve-select-provider__no-provider__inner"
          href={settings('options.no_tv_provider')}
        >
          {settings('strings.select.no_option')}
        </a>
      </div>
    </Modal>
  );
};

Providers.defaultProps = {
  isOpen: false,
  setLocalSelectedProvider: () => {},
};

Providers.propTypes = {
  all: PropTypes.arrayOf(PropTypes.shape(providerType)).isRequired,
  isOpen: PropTypes.bool,
  primary: PropTypes.arrayOf(PropTypes.string).isRequired,
  setIsOpen: PropTypes.func.isRequired,
  setLocalSelectedProvider: PropTypes.func,
};

export default Providers;
